import { useState } from 'react';
import { toast } from 'sonner';
import { ArrowRightLeft, Eye, EyeOff, History, Image as ImageIcon, Pencil, Play, Replace, Star, Trash } from 'lucide-react';

import {
  EDITABLE_FLAGS,
  useAssetHistoryQuery,
  useMakeCoverMutation,
  useMoveMutation,
  usePatchAssetMutation,
  usePrivacyMutation,
  usePurgeMutation,
  useReplaceMutation,
} from '@/client/api/assets/actions';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import { getApiErrorMessage } from '@/client/utils/errors';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import JobLauncherDialog from './JobLauncherDialog';

type Mode = 'edit' | 'privacy' | 'move' | 'replace' | 'purge' | null;

const errBody = (e: any) => {
  const b = e?.data ?? e?.response?.data ?? {};
  return b?.data && typeof b.data === 'object' ? b.data : b;
};

/** Every other thing an admin can do to one photo. */
const AssetManagePanel = ({ asset }: { asset: Asset }) => {
  const asid = asset.asid!;
  const { hasPermission: canUpdate, isSuperAdmin } = useHasPermission('asset.update');
  const { hasPermission: canPurge } = useHasPermission('asset.purge');
  const { hasPermission: canRun } = useHasPermission('asset.run');
  const may = (ok: boolean) => ok || isSuperAdmin;
  const [mode, setMode] = useState<Mode>(null);
  const [reason, setReason] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [reprocess, setReprocess] = useState(false);

  const patch = usePatchAssetMutation();
  const privacy = usePrivacyMutation();
  const cover = useMakeCoverMutation();
  const move = useMoveMutation();
  const replace = useReplaceMutation();
  const purge = usePurgeMutation();
  const history = useAssetHistoryQuery(asid, showHistory);

  const [fields, setFields] = useState<Record<string, any>>({});
  const [eid, setEid] = useState('');
  const [movePlan, setMovePlan] = useState<any>(null);
  const [file, setFile] = useState<File | null>(null);
  const [purgePlan, setPurgePlan] = useState<any>(null);
  const [confirmId, setConfirmId] = useState('');

  const open = (m: Mode) => {
    setMode(m);
    setReason('');
    setMovePlan(null);
    setPurgePlan(null);
    setConfirmId('');
    setFile(null);
    if (m === 'edit') {
      const f: Record<string, any> = { name: asset.name ?? '', description: (asset as any).description ?? '' };
      EDITABLE_FLAGS.forEach(([k]) => { f[k] = (asset as any)[k] === 1; });
      setFields(f);
    }
    if (m === 'purge') {
      purge.mutateAsync({ asid, dry_run: true }).then(setPurgePlan).catch((e) => setPurgePlan({ error: getApiErrorMessage(e, 'Cannot purge') }));
    }
  };

  const run = async (fn: () => Promise<any>, ok: string) => {
    try {
      await fn();
      toast.success(ok);
      setMode(null);
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'That did not work'));
    }
  };

  const deleted = asset.deleted === 1;
  const live = asset.active === 1 && !deleted && asset.private !== 1;

  return (
    <div>
      <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold"><ImageIcon className="h-4 w-4" /> Manage</h4>
      <div className="flex flex-wrap gap-2">
        {may(canUpdate) && (
          <>
            <Button size="sm" variant="outline" onClick={() => open('edit')}><Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit details</Button>
            {live && asset.eid && asset.cover !== 1 && (
              <Button size="sm" variant="outline" disabled={cover.isPending}
                onClick={() => run(() => cover.mutateAsync({ asid }), 'Now the listing cover')}><Star className="mr-1.5 h-3.5 w-3.5" /> Make cover</Button>
            )}
            {asset.encrypted !== 1 && (
              <Button size="sm" variant="outline" onClick={() => open('privacy')}>
                {asset.private === 1 ? <><Eye className="mr-1.5 h-3.5 w-3.5" /> Make public</> : <><EyeOff className="mr-1.5 h-3.5 w-3.5" /> Make private</>}
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => open('move')}><ArrowRightLeft className="mr-1.5 h-3.5 w-3.5" /> Move…</Button>
            {asset.eid && !deleted && <Button size="sm" variant="outline" onClick={() => open('replace')}><Replace className="mr-1.5 h-3.5 w-3.5" /> Replace file…</Button>}
          </>
        )}
        {may(canRun) && <Button size="sm" variant="outline" onClick={() => setReprocess(true)}><Play className="mr-1.5 h-3.5 w-3.5" /> Re-process…</Button>}
        {deleted && may(canPurge) && <Button size="sm" variant="outline" className="text-red-700" onClick={() => open('purge')}><Trash className="mr-1.5 h-3.5 w-3.5" /> Purge files…</Button>}
        <Button size="sm" variant="ghost" onClick={() => setShowHistory((s) => !s)}><History className="mr-1.5 h-3.5 w-3.5" /> History</Button>
      </div>

      {showHistory && (
        <div className="mt-3 max-h-72 overflow-y-auto rounded-md border p-2 text-xs">
          {history.isLoading ? <Skeleton className="h-16" /> : (
            <>
              {(history.data?.admin_actions ?? []).map((x, i) => (
                <div key={`a${i}`} className="border-b py-1 last:border-0">
                  <span className="text-gray-400">{x.at ? new Date(x.at).toLocaleString() : ''}</span>{' '}
                  <b>{x.action.replace(/^asset\./, '')}</b> by {String(x.actor ?? 'unknown')}
                  {x.changes && Object.keys(x.changes).length > 0 && (
                    <span className="text-gray-600"> · {Object.entries(x.changes).map(([k, v]) => `${k}: ${v.from ?? '—'} → ${v.to ?? '—'}`).join(', ')}</span>
                  )}
                  {x.meta && (x.meta as any).reason ? <span className="text-gray-500"> — {(x.meta as any).reason}</span> : null}
                </div>
              ))}
              {(history.data?.changes ?? []).map((c, i) => (
                <div key={`c${i}`} className="border-b py-1 last:border-0">
                  <span className="text-gray-400">{c.at ? new Date(c.at).toLocaleString() : ''}</span>{' '}
                  {Object.entries(c.changes).map(([k, [a, b]]) => `${k}: ${a ?? '—'} → ${b ?? '—'}`).join(' · ')}
                </div>
              ))}
              {!history.data?.admin_actions.length && !history.data?.changes.length && <p className="text-gray-500">No recorded changes.</p>}
            </>
          )}
        </div>
      )}

      <Dialog open={!!mode} onOpenChange={(o) => !o && setMode(null)}>
        <DialogContent className="max-w-lg">
          {mode === 'edit' && (
            <>
              <DialogHeader><DialogTitle>Edit photo #{asid}</DialogTitle></DialogHeader>
              <Input placeholder="Name" value={fields.name ?? ''} onChange={(e) => setFields((f) => ({ ...f, name: e.target.value }))} />
              <textarea className="h-24 w-full rounded-md border p-2 text-sm" placeholder="Description / alt text"
                value={fields.description ?? ''} onChange={(e) => setFields((f) => ({ ...f, description: e.target.value }))} />
              <div className="grid grid-cols-3 gap-2 text-sm">
                {EDITABLE_FLAGS.map(([k, l]) => (
                  <label key={k} className="flex items-center gap-2">
                    <input type="checkbox" checked={!!fields[k]} onChange={(e) => setFields((f) => ({ ...f, [k]: e.target.checked }))} /> {l}
                  </label>
                ))}
              </div>
              <DialogFooter>
                <Button disabled={patch.isPending} onClick={() => run(() => {
                  const out: Record<string, unknown> = {};
                  Object.entries(fields).forEach(([k, v]) => {
                    const cur = (asset as any)[k];
                    const now = typeof v === 'boolean' ? (v ? 1 : 0) : v;
                    if ((cur ?? (typeof v === 'boolean' ? 0 : '')) !== now) out[k] = now;
                  });
                  return patch.mutateAsync({ asid, fields: out });
                }, 'Saved')}>Save</Button>
              </DialogFooter>
            </>
          )}

          {mode === 'privacy' && (
            <>
              <DialogHeader>
                <DialogTitle>{asset.private === 1 ? 'Make public' : 'Make private'}?</DialogTitle>
                <DialogDescription>
                  {asset.private === 1 ? 'Every stored size becomes publicly readable now.' : 'Every stored size becomes private now, and the public Uploadcare copy is deleted.'}
                </DialogDescription>
              </DialogHeader>
              <Input placeholder="Reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} />
              <DialogFooter>
                <Button disabled={!reason.trim() || privacy.isPending}
                  onClick={() => run(() => privacy.mutateAsync({ asid, private: asset.private !== 1, reason: reason.trim() }), 'Privacy changed')}>Confirm</Button>
              </DialogFooter>
            </>
          )}

          {mode === 'move' && (
            <>
              <DialogHeader>
                <DialogTitle>Move photo #{asid}</DialogTitle>
                <DialogDescription>To another listing, last in its order. If that listing has a different owner this is a transfer and the photo becomes theirs. Both owners are told.</DialogDescription>
              </DialogHeader>
              <div className="flex gap-2">
                <Input placeholder="Target listing id" inputMode="numeric" value={eid} onChange={(e) => { setEid(e.target.value.replace(/\D/g, '')); setMovePlan(null); }} />
                <Button variant="outline" disabled={!eid || move.isPending} onClick={async () => {
                  try { setMovePlan(await move.mutateAsync({ asid, eid: Number(eid), dry_run: true })); }
                  catch (e) { setMovePlan({ error: getApiErrorMessage(e, 'Cannot move'), ...errBody(e) }); }
                }}>Check</Button>
              </div>
              {movePlan && (
                <div className={`rounded-md p-2 text-sm ${movePlan.error ? 'bg-red-50 text-red-700' : 'bg-gray-50'}`}>
                  {movePlan.error ?? <>To “{movePlan.to_name}” (#{movePlan.to_eid}). {movePlan.transfer ? <b className="text-amber-700">Transfer: owner {movePlan.from_owner ?? 'none'} → {movePlan.to_owner}</b> : 'Same owner.'}</>}
                </div>
              )}
              <Input placeholder="Reason (required, the owners see it)" value={reason} onChange={(e) => setReason(e.target.value)} />
              <DialogFooter>
                <Button disabled={!movePlan || movePlan.error || !reason.trim() || move.isPending}
                  onClick={() => run(() => move.mutateAsync({ asid, eid: Number(eid), reason: reason.trim() }), 'Moved')}>Move</Button>
              </DialogFooter>
            </>
          )}

          {mode === 'replace' && (
            <>
              <DialogHeader>
                <DialogTitle>Replace photo #{asid}</DialogTitle>
                <DialogDescription>The new file goes through the normal upload checks, takes this photo's place and cover, and this one is deleted (restorable). The seller is told.</DialogDescription>
              </DialogHeader>
              <input type="file" accept="image/*,video/*" aria-label="Replacement file" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
              <Input placeholder="Reason (required, the seller sees it)" value={reason} onChange={(e) => setReason(e.target.value)} />
              <DialogFooter>
                <Button disabled={!file || !reason.trim() || replace.isPending}
                  onClick={() => run(() => replace.mutateAsync({ asid, file: file!, reason: reason.trim() }), 'Replaced')}>
                  {replace.isPending ? 'Uploading…' : 'Replace'}
                </Button>
              </DialogFooter>
            </>
          )}

          {mode === 'purge' && (
            <>
              <DialogHeader>
                <DialogTitle>Permanently remove the files of #{asid}?</DialogTitle>
                <DialogDescription>Deletes every stored size, the R2 copy and the Uploadcare copy. This cannot be undone. The record and its history stay.</DialogDescription>
              </DialogHeader>
              {!purgePlan ? <Skeleton className="h-16" /> : purgePlan.error ? (
                <p className="rounded-md bg-red-50 p-2 text-sm text-red-700">{purgePlan.error}</p>
              ) : (
                <div className="max-h-40 overflow-y-auto rounded-md bg-gray-50 p-2 text-xs">
                  <div>Deleted {purgePlan.deleted_days} days ago.</div>
                  {(purgePlan.spaces_keys ?? []).map((k: string) => <div key={k} className="font-mono">{k}</div>)}
                  {purgePlan.spaces_note && <div>{purgePlan.spaces_note}</div>}
                  {purgePlan.r2 && <div className="font-mono">R2: {purgePlan.r2.join('/')}</div>}
                  {purgePlan.uploadcare && <div className="font-mono">Uploadcare: {purgePlan.uploadcare}</div>}
                </div>
              )}
              <Input placeholder="Reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} />
              <Input placeholder={`Type ${asid} to confirm`} value={confirmId} onChange={(e) => setConfirmId(e.target.value)} />
              <DialogFooter>
                <Button className="bg-red-600 hover:bg-red-700" disabled={!purgePlan || purgePlan.error || !reason.trim() || Number(confirmId) !== asid || purge.isPending}
                  onClick={() => run(() => purge.mutateAsync({ asid, reason: reason.trim(), confirm_asid: asid }), 'Files removed')}>Purge</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <JobLauncherDialog open={reprocess} onClose={() => setReprocess(false)} scope={{ asids: [asid] }} scopeLabel={`photo #${asid}`}
        initial={{ kind: 'signal', processors: ['task:asset-local-metrics'], mode: 'force' }} />
    </div>
  );
};

export default AssetManagePanel;
